import { Stack, styled, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import React, { useContext } from "react";
import { pxToRem } from "../../design/utils";
import { SeparatorLineHorizontal } from "../bits/layout";
import { ColourOption } from "../bits/settings/ColourOption";
import type { fontMap } from "../bits/settings/defaults";
import {
  fontOptionsSansSerif,
  fontOptionsSerif,
  pageColourOptions,
} from "../bits/settings/defaults";
import { FontOption, FontOptionsWrapper } from "../bits/settings/FontOption";
import { FootnoteOption } from "../bits/settings/FootnoteOption";
import { SettingsContext } from "../contexts/WithSettings/SettingsContext";

const MenuHeading = styled("h2")(({ theme }) => ({
  ...theme.customTypography.label._1,
  color: theme.colours.accent.dark._30,
  userSelect: "none",
}));

const StyledSettingsMenu = styled("div")`
  position: absolute;
  top: ${pxToRem(46)};
  right: 0;
  z-index: 20;
  width: 308px;
  height: calc(100vh - ${pxToRem(46)});
  height: calc(100dvh - ${pxToRem(46)});
  box-shadow:
    0px 0px 12px -6px rgba(199,196,183, 1),
    0px 0px 4px 2px rgba(211,209,199, 1),
    0px 0px 0px 0.5px rgba(250,249,244, 1),
    0px -6px 12px -2px rgba(255,255,255, 1);
  opacity: 1;
  background-color: rgba(237,236,229, 1);
  padding: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  ${({ theme }) => theme.layers.relief.accent_30.shadow};
}`;

export const SettingsMenu = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>((props, ref) => {
  const { ereaderSettings, setEreaderSettings } = useContext(SettingsContext);
  const theme = useTheme();

  const updateColour = (colour: string | undefined) => {
    if (colour === ereaderSettings?.pageColour) return;
    setEreaderSettings({
      ...(ereaderSettings || {}),
      pageColour: colour,
    });
  };

  const updateFont = (font: string | undefined) => {
    if (font === ereaderSettings?.font) return;
    setEreaderSettings({
      ...(ereaderSettings || {}),
      font,
    });
  };

  const updateFontSize = (fontSize: number | undefined) => {
    if (fontSize === ereaderSettings?.fontSize) return;
    setEreaderSettings({
      ...(ereaderSettings || {}),
      fontSize,
    });
  };

  const updateFootnoteVisibility = (
    footnotes: PrismaJson.EreaderSettings["footnotes"] | undefined,
  ) => {
    if (footnotes === ereaderSettings?.footnotes) return;
    setEreaderSettings({
      ...(ereaderSettings || {}),
      footnotes,
    });
  };

  return (
    <StyledSettingsMenu ref={ref} {...props}>
      <MenuHeading style={{ paddingBottom: "4px" }}>Typeface</MenuHeading>
      <div
        css={({ customTypography }) => ({
          ...customTypography.overline._3,
          userSelect: "none",
        })}
      >
        SERIF
      </div>
      <FontOptionsWrapper
        style={{
          paddingTop: "4px",
          paddingBottom: "8px",
        }}
      >
        <FontOption updateFont={updateFont} selected={!ereaderSettings?.font} />
        {fontOptionsSerif.map((font) => (
          <FontOption
            key={font}
            updateFont={updateFont}
            FontLabel={font as keyof typeof fontMap}
            selected={ereaderSettings?.font === font}
          />
        ))}
      </FontOptionsWrapper>

      <div
        css={({ customTypography }) => ({
          ...customTypography.overline._3,
          userSelect: "none",
        })}
      >
        SANS-SERIF
      </div>
      <FontOptionsWrapper
        style={{
          padding: "8px 0px",
        }}
      >
        {fontOptionsSansSerif.map((font) => (
          <FontOption
            key={font}
            updateFont={updateFont}
            FontLabel={font as keyof typeof fontMap}
            selected={ereaderSettings?.font === font}
          />
        ))}
      </FontOptionsWrapper>
      <SeparatorLineHorizontal />
      <MenuHeading>Font size</MenuHeading>
      <Box sx={{ width: 280 }}>
        <Stack spacing={2} direction="row" sx={{ alignItems: "center", mb: 1 }}>
          <p
            style={{
              fontSize: "12px",
              userSelect: "none",
            }}
          >
            A
          </p>
          <Slider
            aria-label="font size"
            getAriaValueText={(value: number) => `${value}px`}
            valueLabelDisplay="auto"
            color="secondary"
            step={2}
            marks={true}
            min={10}
            max={22}
            size="medium"
            value={ereaderSettings?.fontSize || 16}
            onChange={(_, newValue) => {
              updateFontSize(newValue);
            }}
          />
          <p style={{ fontSize: "22px", userSelect: "none" }}>A</p>
        </Stack>
      </Box>
      <SeparatorLineHorizontal />
      <MenuHeading>Page colour</MenuHeading>
      <ColourOption
        updateColour={updateColour}
        colourBackground={theme.colours.accent.light._20}
      />
      <ColourOption
        updateColour={updateColour}
        colourBackground="#fff"
        colourLabel="white"
      />
      <ColourOption
        updateColour={updateColour}
        colourBackground={pageColourOptions.black.background}
        colourLabel="black"
      />
      <ColourOption
        updateColour={updateColour}
        colourBackground={pageColourOptions.cream.background}
        colourLabel="cream"
      />
      <ColourOption
        updateColour={updateColour}
        colourBackground={pageColourOptions.green.background}
        colourLabel="green"
      />
      <SeparatorLineHorizontal />
      <MenuHeading>Footnotes</MenuHeading>
      <FontOptionsWrapper
        style={{
          padding: "8px 0px",
        }}
      >
        <FootnoteOption
          updateFootnoteOption={updateFootnoteVisibility}
          footnoteLabel="clickToShow"
          selected={ereaderSettings?.footnotes !== "inline"}
        />
        <FootnoteOption
          updateFootnoteOption={updateFootnoteVisibility}
          footnoteLabel="inline"
          selected={ereaderSettings?.footnotes === "inline"}
        />
      </FontOptionsWrapper>
      <SeparatorLineHorizontal />
    </StyledSettingsMenu>
  );
});
